// ═══════════════════════════════════════════════════════════════
// RollCall — Edit Personnel
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title, Paper, Group, Button, TextInput, Select, Textarea, Center, Loader,
} from '@mantine/core';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { FIELDS } from '../../lib/api-switch';
import { useRollCallStore } from '../../store/rollcallStore';
import { ICS_POSITIONS } from '../../types';

export default function PersonnelEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { personnel, loadPersonnel, updatePersonnel } = useRollCallStore();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, string> | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => { loadPersonnel(); }, []);

  useEffect(() => {
    const person = personnel.find(p => String(p.id) === id);
    if (person && !form) {
      setForm({
        firstName: person.firstName,
        lastName: person.lastName,
        agency: person.agency,
        position: person.position,
        pin: person.pin,
        badgeId: person.badgeId,
        phone: person.phone,
        email: person.email,
        notes: person.notes,
        status: person.status,
      });
    }
  }, [id, personnel]);

  if (!form) return <Center py="xl"><Loader /></Center>;

  const update = (field: string, value: string) => {
    setForm(prev => prev ? { ...prev, [field]: value } : prev);
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.firstName.trim()) errs.firstName = 'Required';
    if (!form.lastName.trim()) errs.lastName = 'Required';
    if (!form.agency.trim()) errs.agency = 'Required';
    if (!form.pin.trim()) errs.pin = 'Required';
    else if (!/^\d{4,6}$/.test(form.pin)) errs.pin = 'PIN must be 4-6 digits';
    else if (personnel.some(p => p.pin === form.pin && String(p.id) !== id)) errs.pin = 'PIN already in use';
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      notifications.show({ title: 'Error', message: 'Please fix validation errors', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      const F = FIELDS.personnel;
      await updatePersonnel(Number(id), [
        [F.firstName, form.firstName.trim()],
        [F.lastName, form.lastName.trim()],
        [F.agency, form.agency.trim()],
        [F.position, form.position || ''],
        [F.pin, form.pin],
        [F.badgeId, form.badgeId.trim()],
        [F.phone, form.phone.trim()],
        [F.email, form.email.trim()],
        [F.notes, form.notes.trim()],
        [F.status, form.status],
      ]);
      notifications.show({ title: 'Updated', message: 'Personnel updated', color: 'green' });
      navigate('/rollcall/personnel');
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err?.message || 'Failed to update', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Edit Personnel — {form.firstName} {form.lastName}</Title>
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/rollcall/personnel')}>Cancel</Button>
      </Group>

      <Paper p="lg" radius="sm" withBorder maw={640}>
        <Group grow mb="md">
          <TextInput label="First Name" required value={form.firstName} error={errors.firstName}
            onChange={e => update('firstName', e.currentTarget.value)} />
          <TextInput label="Last Name" required value={form.lastName} error={errors.lastName}
            onChange={e => update('lastName', e.currentTarget.value)} />
        </Group>

        <TextInput label="Agency / Organization" required value={form.agency} error={errors.agency}
          onChange={e => update('agency', e.currentTarget.value)} mb="md" />

        <Select label="ICS Position" placeholder="Select position" data={ICS_POSITIONS} clearable
          value={form.position || null} onChange={v => update('position', v ?? '')} mb="md" />

        <Group grow mb="md">
          <TextInput label="PIN" required placeholder="4-6 digits" value={form.pin} error={errors.pin}
            onChange={e => update('pin', e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
            description="Unique numeric PIN for kiosk check-in" />
          <TextInput label="Badge ID" placeholder="QR or barcode value" value={form.badgeId}
            onChange={e => update('badgeId', e.currentTarget.value)} />
        </Group>

        <Group grow mb="md">
          <TextInput label="Phone" value={form.phone} onChange={e => update('phone', e.currentTarget.value)} />
          <TextInput label="Email" value={form.email} onChange={e => update('email', e.currentTarget.value)} />
        </Group>

        <Select label="Status" data={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
          value={form.status} onChange={v => update('status', v ?? 'active')} mb="md" />

        <Textarea label="Notes" value={form.notes} onChange={e => update('notes', e.currentTarget.value)} mb="lg" />

        <Group justify="flex-end">
          <Button variant="light" onClick={() => navigate('/rollcall/personnel')}>Cancel</Button>
          <Button onClick={handleSave} loading={saving} leftSection={<IconCheck size={14} />}>
            Save Changes
          </Button>
        </Group>
      </Paper>
    </div>
  );
}
